import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, X, Search } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children, hint }) {
  return <div><label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>{children}{hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}</div>;
}

export default function CancellationPolicies() {
  const confirm = useConfirm();
  const [policies, setPolicies] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);     // { editing? } | null
  const [form, setForm] = useState({ name: '', charge_percentage: 100, pay_worker: false, max_payment_hours: '', description: '', is_default: false, is_active: true });
  const [saving, setSaving] = useState(false);
  const [reasonInput, setReasonInput] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([api.get('/cancellations/policies'), api.get('/cancellations/reasons')]);
      setPolicies(p.data || []); setReasons(r.data || []);
    } catch { toast.error('Failed to load cancellation settings'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ name: '', charge_percentage: 100, pay_worker: false, max_payment_hours: '', description: '', is_default: false, is_active: true }); setModal({}); };
  const openEdit = (p) => { setForm({ name: p.name, charge_percentage: p.charge_percentage, pay_worker: !!p.pay_worker, max_payment_hours: p.max_payment_hours ?? '', description: p.description || '', is_default: p.is_default, is_active: p.is_active }); setModal({ editing: p }); };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      if (modal.editing) await api.put(`/cancellations/policies/${modal.editing.id}`, form);
      else await api.post('/cancellations/policies', form);
      toast.success('Saved'); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };
  const del = async (p) => { if (!await confirm(`Deactivate "${p.name}"?`, { title: 'Deactivate Policy', danger: true })) return; try { await api.delete(`/cancellations/policies/${p.id}`); load(); } catch { toast.error('Failed'); } };
  const addReason = async () => { const l = reasonInput.trim(); if (!l) return; try { await api.post('/cancellations/reasons', { label: l }); setReasonInput(''); load(); } catch { toast.error('Failed'); } };
  const delReason = async (r) => { try { await api.delete(`/cancellations/reasons/${r.id}`); load(); } catch { toast.error('Failed'); } };

  if (loading) return <div className="flex items-center justify-center h-40"><div className="w-7 h-7 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div><h2 className="text-sm font-bold text-slate-900">Cancellation Policies</h2><p className="text-xs text-slate-400">Charge % is what NDIS bills the participant for a short-notice cancellation (often 100%). Whether the rostered worker is paid is a separate choice on each policy — and max payment hours caps it.</p></div>
          <button onClick={openNew} className="btn-primary btn-sm flex items-center gap-1.5"><Plus size={13} /> New Policy</button>
        </div>
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-xs font-medium text-slate-500"><th className="text-left px-4 py-3">Name</th><th className="text-center px-3 py-3">Charge %</th><th className="text-center px-3 py-3">Worker Pay</th><th className="text-center px-3 py-3">Default</th><th className="text-center px-3 py-3">Active</th><th className="px-3 py-3" /></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {policies.map(p => (
                <tr key={p.id}>
                  <td className="px-4 py-3"><span className="font-semibold text-slate-800">{p.name}</span>{p.description && <p className="text-xs text-slate-400 mt-0.5 max-w-md">{p.description}</p>}</td>
                  <td className="px-3 py-3 text-center font-semibold text-slate-700">{p.charge_percentage}%</td>
                  <td className="px-3 py-3 text-center text-slate-600">{!p.pay_worker ? 'Not paid' : (p.max_payment_hours != null ? `Up to ${p.max_payment_hours}h` : 'Full shift')}</td>
                  <td className="px-3 py-3 text-center">{p.is_default && <span className="px-2 py-0.5 rounded-full text-xs bg-primary-50 text-primary-700">Default</span>}</td>
                  <td className="px-3 py-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs ${p.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>{p.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td className="px-3 py-3"><div className="flex justify-end gap-1"><button onClick={() => openEdit(p)} className="p-1.5 text-slate-400 hover:text-primary-500 hover:bg-primary-50 rounded"><Pencil size={13} /></button><button onClick={() => del(p)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 size={13} /></button></div></td>
                </tr>
              ))}
              {policies.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-8 text-sm">No policies yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-sm font-bold text-slate-900 mb-1">Cancellation Reasons</h2>
        <p className="text-xs text-slate-400 mb-3">Selectable when cancelling a shift.</p>
        <div className="flex gap-2 max-w-md">
          <input className="input-field flex-1" placeholder="Add a reason…" value={reasonInput} onChange={e => setReasonInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addReason(); } }} />
          <button onClick={addReason} className="btn-secondary btn-sm">Add</button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {reasons.filter(r => r.is_active).map(r => (
            <span key={r.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-slate-100 text-slate-700 border border-slate-200">
              {r.label}<button onClick={() => delReason(r)} className="hover:text-red-500"><X size={11} /></button>
            </span>
          ))}
          {reasons.filter(r => r.is_active).length === 0 && <span className="text-sm text-slate-400">No reasons yet.</span>}
        </div>
      </div>

      {modal && (
        <Modal title={modal.editing ? 'Edit Policy' : 'New Cancellation Policy'} onClose={() => setModal(null)}>
          <Field label="Name"><input className="input-field" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Short notice (NDIS 100%)" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Charge %" hint="0–100% of service cost"><input type="number" min="0" max="100" className="input-field" value={form.charge_percentage} onChange={e => setForm(f => ({ ...f, charge_percentage: e.target.value }))} /></Field>
            <Field label="Max Payment Hours" hint={form.pay_worker ? 'Blank = pay the whole shift' : 'Policy does not pay the worker'}>
              <input type="number" min="0" step="0.5" className="input-field" value={form.max_payment_hours} disabled={!form.pay_worker}
                onChange={e => setForm(f => ({ ...f, max_payment_hours: e.target.value }))} placeholder="e.g. 2" />
            </Field>
          </div>
          <Field label="Description"><textarea className="input-field" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></Field>
          <label className="flex items-start gap-2 text-sm text-slate-600">
            <input type="checkbox" className="mt-0.5" checked={form.pay_worker} onChange={e => setForm(f => ({ ...f, pay_worker: e.target.checked, max_payment_hours: e.target.checked ? f.max_payment_hours : '' }))} />
            <span>Pay the rostered worker
              <span className="block text-xs text-slate-400">Creates an approved timesheet for the cancelled shift. Leave off for policies where only the client is charged.</span>
            </span>
          </label>
          <div className="flex gap-5">
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.is_default} onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))} /> Default (pre-selected)</label>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} /> Active</label>
          </div>
          <div className="flex justify-end gap-3 pt-2"><button onClick={() => setModal(null)} className="btn-secondary btn-sm">Cancel</button><button onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? 'Saving…' : 'Save'}</button></div>
        </Modal>
      )}
    </div>
  );
}
